import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Box, Container, Grid, Typography, Divider } from '@mui/material';
import '../styles/Footer.css';

const DiscordIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

const VoteIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 14l-5-5 1.41-1.41L12 14.17l7.59-7.59L21 8l-9 9z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [isLoggedIn, setIsLoggedIn] = useState(!!localStorage.getItem('user'));

  useEffect(() => {
    const sync = () => setIsLoggedIn(!!localStorage.getItem('user'));
    window.addEventListener('storage', sync);
    return () => window.removeEventListener('storage', sync);
  }, []);

  const navLinks = [
    { to: '/', label: 'Accueil' },
    { to: '/players', label: 'Ladder' },
    { to: '/vote', label: 'Voter' },
    { to: '/join-us', label: 'Rejoindre' },
    ...(!isLoggedIn ? [
      { to: '/signup', label: "S'inscrire" },
      { to: '/login', label: 'Connexion' },
    ] : []),
  ];

  const communityLinks = [
    { href: 'https://discord.gg/dTuaMqvSAr', label: 'Serveur Discord', external: true },
    { to: '/vote', label: 'Voter (via notre site)' },
    { to: '/vote', label: 'Recompenses de vote' },
    { to: '/join-us', label: 'Nous rejoindre' },
  ];

  return (
    <footer className="footer-Nexus">
      {/* Bordure superieure degradee */}
      <div className="footer-top-border" />

      {/* Failles decoratives */}
      <div className="footer-rifts" aria-hidden="true">
        <div className="footer-rift footer-rift-1" />
        <div className="footer-rift footer-rift-2" />
        <div className="footer-rift footer-rift-3" />
      </div>

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
        {/* Corps principal */}
        <Box className="footer-body">
          <Grid container spacing={5}>

            {/* Colonne 1 : Branding */}
            <Grid item xs={12} md={4}>
              <Box className="footer-brand">
                <Typography variant="h4" className="footer-logo">
                  Nexus
                </Typography>
                <Typography className="footer-tagline">
                  Parcourez les fractures du temps
                </Typography>
                <Typography className="footer-description">
                  Un monde ou les failles temporelles
                  faconnent chaque aventure. Rejoignez des Voyageurs
                  et ecrivez votre legende dans les Nexus du temps.
                </Typography>

                {/* Icones sociales */}
                <Box className="footer-socials">
                  <a
                    href="https://discord.gg/dTuaMqvSAr"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="footer-social-btn footer-social-discord"
                    aria-label="Discord"
                  >
                    <DiscordIcon />
                    <span>Discord</span>
                  </a>
                  <Link
                    to="/vote"
                    className="footer-social-btn footer-social-vote"
                    aria-label="Voter"
                  >
                    <VoteIcon />
                    <span>Voter</span>
                  </Link>
                </Box>
              </Box>
            </Grid>

            {/* Colonne 2 : Navigation */}
            <Grid item xs={6} md={2}>
              <Typography className="footer-col-title">Navigation</Typography>
              <ul className="footer-link-list">
                {navLinks.map((link) => (
                  <li key={link.to}>
                    <Link to={link.to} className="footer-link">
                      <span className="footer-link-arrow">&gt;</span>
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Grid>

            {/* Colonne 3 : Communaute */}
            <Grid item xs={6} md={4}>
              <Typography className="footer-col-title">Communaute</Typography>
              <ul className="footer-link-list">
                {communityLinks.map((link, i) => (
                  <li key={i}>
                    {link.external ? (
                      <a
                        href={link.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="footer-link"
                      >
                        <span className="footer-link-arrow">&gt;</span>
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.to} className="footer-link">
                        <span className="footer-link-arrow">&gt;</span>
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </Grid>

          </Grid>
        </Box>

        {/* Bas du footer */}
        <Divider className="footer-divider" />
        <Box className="footer-bottom">
          <Typography className="footer-copyright">
            © {currentYear} <strong>Nexus</strong>. Tous droits reserves.
          </Typography>
          <Typography className="footer-disclaimer">
            Site non officiel - Propriete intellectuelle protegee
          </Typography>
        </Box>
      </Container>
    </footer>
  );
};

export default Footer;
